import * as React from "react";

import { CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, Modal, renderMarkdown, Tooltip } from "@impero-com/ui";
import { Table, Popconfirm } from "antd";
import { isEqual } from "lodash-es";

import {
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod,
    OffsetAnchor,
    OffsetType,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";

import { DEFAULT_DUE_DATE_OFFSET } from "../../types";
import { MultiDefaultDueDateOffsetInput } from "../multiDefaultDueDateOffsetInput";
import { GenericOccurrencesArray } from "../rulesWithArrays";
import * as theme from "../theme.css";

export type Interval = "Month" | "TwoMonths" | "Quarter" | "HalfYear";

interface BaseGenericCustomDueDatesModalProps<OccurrenceType extends string> {
    initialCurrentOccurrences: GenericOccurrencesArray<OccurrenceType>;
    translateOccurrence: (occurrence: OccurrenceType) => string;
    translateOccurrenceShort: (occurrence: OccurrenceType) => string;
    allOccurrences: OccurrenceType[];
    interval: Interval;
}

interface EditGenericCustomDueDatesModalProps<
    OccurrenceType extends string,
> extends BaseGenericCustomDueDatesModalProps<OccurrenceType> {
    type: "EDIT";
    onChange: (
        currentOccurrences: Partial<
            Record<OccurrenceType, SchedulingOccurrenceChange>
        >
    ) => void;
}

interface ReadOnlyGenericCustomDueDatesModalProps<
    OccurrenceType extends string,
> extends BaseGenericCustomDueDatesModalProps<OccurrenceType> {
    type: "READ_ONLY";
}

type GenericCustomDueDatesModalProps<OccurrenceType extends string> =
    | EditGenericCustomDueDatesModalProps<OccurrenceType>
    | ReadOnlyGenericCustomDueDatesModalProps<OccurrenceType>;

interface GenericCustomDueDatesModalState<OccurrenceType extends string> {
    currentOccurrences: Record<OccurrenceType, RowState<MonthDueDateOffset>>;
    changedOccurrences: Partial<
        Record<OccurrenceType, SchedulingOccurrenceChange>
    >;
    modalVisible: boolean;
}

class GenericCustomDueDatesModal<
    OccurrenceType extends string,
> extends React.Component<
    GenericCustomDueDatesModalProps<OccurrenceType>,
    GenericCustomDueDatesModalState<OccurrenceType>
> {
    constructor(props: GenericCustomDueDatesModalProps<OccurrenceType>) {
        super(props);
        this.state = {
            currentOccurrences: this.buildCurrentOccurrences(),
            changedOccurrences: {},
            modalVisible: false,
        };
    }

    private cancelModal = () => {
        this.setState({
            modalVisible: false,
        });
    };

    private buildCurrentOccurrences(): Record<
        OccurrenceType,
        RowState<MonthDueDateOffset>
    > {
        const { initialCurrentOccurrences, allOccurrences } = this.props;
        return Object.assign(
            {},
            ...allOccurrences.map((occurrence) => ({
                [occurrence]: stateTypesToRowState(
                    initialCurrentOccurrences[occurrence].map(
                        calculateOccurrenceStateType
                    )
                ),
            }))
        );
    }

    private updateCurrentOccurrences(
        changes: Partial<Record<OccurrenceType, SchedulingOccurrenceChange>>
    ): Record<OccurrenceType, RowState<MonthDueDateOffset>> {
        const { allOccurrences } = this.props;
        const current = this.buildCurrentOccurrences();
        allOccurrences.forEach((occurrence) => {
            const change = changes[occurrence];
            const curr = current[occurrence];
            switch (change) {
                case "ActivateOnlyDeactivated":
                    let customizedCount = 0;
                    if (
                        curr.type === "AllActiveAndSomeCustom" ||
                        curr.type === "Mixed"
                    ) {
                        customizedCount = curr.customizedCount;
                    }
                    if (customizedCount > 0) {
                        current[occurrence] = {
                            type: "AllActiveAndSomeCustom",
                            totalCount: curr.totalCount,
                            customizedCount,
                        };
                    } else {
                        current[occurrence] = {
                            type: "AllActiveAndDefault",
                            totalCount: curr.totalCount,
                        };
                    }
                    break;
                case "Default":
                    current[occurrence] = {
                        type: "AllActiveAndDefault",
                        totalCount: curr.totalCount,
                    };
                    break;
                case undefined:
                    break;
                default: // is actually { Custom: MonthDueDateOffset }
                    current[occurrence] = {
                        type: "AllCustomAndSame",
                        offset: change.Custom,
                        totalCount: curr.totalCount,
                    };
                    break;
            }
        });
        return current;
    }

    private openModal = () => {
        this.setState({
            modalVisible: true,
            currentOccurrences: this.buildCurrentOccurrences(),
            changedOccurrences: {},
        });
    };

    private onOkModal = () => {
        if (this.props.type !== "EDIT") {
            throw new Error("not editable");
        }

        const { changedOccurrences } = this.state;

        this.setState({
            modalVisible: false,
        });
        this.props.onChange(changedOccurrences);
    };

    private getCustomizedOccurrenceLabels(): string[] {
        const {
            allOccurrences,
            initialCurrentOccurrences,
            translateOccurrenceShort,
        } = this.props;
        return allOccurrences
            .filter((occurrence) => {
                return initialCurrentOccurrences[occurrence].some(
                    (t) => t && t !== "Default"
                );
            })
            .map(translateOccurrenceShort);
    }

    render = () => {
        const { allOccurrences, translateOccurrence, interval } = this.props;
        const { currentOccurrences, modalVisible } = this.state;

        const customizedIntervalLabels = this.getCustomizedOccurrenceLabels();

        const onChangeInternal = (
            occurrenceType: OccurrenceType,
            change: SchedulingOccurrenceChange | undefined
        ) => {
            this.setState((prevState) => {
                const { changedOccurrences } = prevState;
                const updatedChangedOccurrences = {
                    ...changedOccurrences,
                    [occurrenceType]: change,
                };

                return {
                    changedOccurrences: updatedChangedOccurrences,
                    currentOccurrences: this.updateCurrentOccurrences(
                        updatedChangedOccurrences
                    ),
                };
            });
        };

        const buttonLabel =
            customizedIntervalLabels.length > 0
                ? t("CreateControl_Recurrence_Interval_CustomDueDateFor") +
                  " " +
                  customizedIntervalLabels.join(", ")
                : t("CreateControl_Recurrence_Interval_SetCustomDueDates");

        let typedProps;
        switch (this.props.type) {
            case "EDIT": {
                typedProps = {
                    type: "EDIT" as const,
                    onChange: onChangeInternal,
                };
                break;
            }
            case "READ_ONLY": {
                typedProps = {
                    type: "READ_ONLY" as const,
                };
                break;
            }
            default: {
                return assertNever(this.props);
            }
        }

        return (
            <BaseFormDisplay
                label={t("Control_Scheduling_Custom_Due_Dates")}
                className={theme.customDueDatesLabel}
            >
                <Button variant="link" onClick={this.openModal}>
                    {buttonLabel}
                </Button>
                <Modal
                    variant="content"
                    open={modalVisible}
                    onClose={this.cancelModal}
                    width={1100}
                    title={t(getModalTitleForInterval(interval))}
                    footer={
                        <>
                            <Button onClick={this.cancelModal}>
                                {t("Cancel")}
                            </Button>
                            <Button variant="primary" onClick={this.onOkModal}>
                                {t("Apply")}
                            </Button>
                        </>
                    }
                >
                    <GenericCustomDueDatesTable<OccurrenceType>
                        {...typedProps}
                        currentOccurrences={currentOccurrences}
                        translateOccurrence={translateOccurrence}
                        allOccurrences={allOccurrences}
                        interval={interval}
                        modalVisible={modalVisible}
                    />
                </Modal>
            </BaseFormDisplay>
        );
    };
}

function getModalTitleForInterval(interval: Interval) {
    switch (interval) {
        case "Month":
            return "CreateControl_Recurrence_IntervalDetails_Title_Months";
        case "TwoMonths":
            return "CreateControl_Recurrence_IntervalDetails_Title_TwoMonths";
        case "Quarter":
            return "CreateControl_Recurrence_IntervalDetails_Title_Quarters";
        case "HalfYear":
            return "CreateControl_Recurrence_IntervalDetails_Title_HalfYears";
    }
}

type RowState<T> =
    // If the month is not active for any selected control, show [Activate] button.
    | { type: "AllInactive"; totalCount: number }
    // If some are deactivated (and others are activated or maybe even customized), show a button along the lines of [Activate this month for the N controls where it's deactivated] (paraphrased).
    | {
          type: "Mixed";
          totalCount: number;
          inactiveCount: number;
          customizedCount: number;
      }
    // If all are activated but none are customized, show [Customize] button. When clicked: Show custom due date edit fields (empty, type="INPUT").
    | { type: "AllActiveAndDefault"; totalCount: number }
    // If all are activated but not all are customized
    | {
          type: "AllActiveAndSomeCustom";
          totalCount: number;
          customizedCount: number;
      }
    // If all are activated and customized (If each field has the same value across all selected control)
    | { type: "AllCustomAndSame"; offset: T; totalCount: number }
    // If all are activated and customized (otherwise)
    | { type: "AllCustomAndDifferent"; totalCount: number };

type OccurrenceStateType<T> =
    | { type: "Inactive" }
    | { type: "ActiveDefault" }
    | { type: "ActiveCustom"; offset: T };

type DefaultOrCustom<T> =
    | Extract<DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod, string>
    | Record<
          keyof Exclude<
              DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod,
              string
          >,
          T
      >;

function calculateOccurrenceStateType<T>(
    value: DefaultOrCustom<T> | undefined
): OccurrenceStateType<T> {
    if (value === undefined) {
        return { type: "Inactive" };
    } else if (value === "Default") {
        return { type: "ActiveDefault" };
    } else {
        return { type: "ActiveCustom", offset: value["Custom"] };
    }
}

function stateTypesToRowState<T>(types: OccurrenceStateType<T>[]): RowState<T> {
    const inactiveCount = types.filter((t) => t.type === "Inactive").length;
    const customizedCount = types.filter(
        (t) => t.type === "ActiveCustom"
    ).length;
    const totalCount = types.length;

    if (types.every((t) => t.type === "Inactive")) {
        return { type: "AllInactive", totalCount };
    } else if (types.some((t) => t.type === "Inactive")) {
        return { type: "Mixed", totalCount, inactiveCount, customizedCount };
    } else if (types.every((t) => t.type === "ActiveDefault")) {
        return { type: "AllActiveAndDefault", totalCount };
    } else if (types.some((t) => t.type === "ActiveDefault")) {
        return { type: "AllActiveAndSomeCustom", totalCount, customizedCount };
    } else if (types.every((t) => isEqual(t, types[0]))) {
        // SAFETY: Inactive and ActiveDefault variants are exlecuded at this point, so the only one remaining is ActiveCustom
        return {
            type: "AllCustomAndSame",
            offset: (types[0] as { type: "ActiveCustom"; offset: T }).offset,
            totalCount,
        };
    } else {
        return { type: "AllCustomAndDifferent", totalCount };
    }
}

interface BaseGenericCustomDueDatesProps<OccurrenceType extends string> {
    translateOccurrence: (occurrence: OccurrenceType) => string;
    currentOccurrences: Record<OccurrenceType, RowState<MonthDueDateOffset>>;
    allOccurrences: OccurrenceType[];
    interval: Interval;
    modalVisible: boolean;
}

interface EditGenericCustomDueDatesProps<
    OccurrenceType extends string,
> extends BaseGenericCustomDueDatesProps<OccurrenceType> {
    type: "EDIT";
    onChange: (
        occurrenceType: OccurrenceType,
        schedulingChange: SchedulingOccurrenceChange | undefined
    ) => void;
}

interface ReadOnlyGenericCustomDueDatesProps<
    OccurrenceType extends string,
> extends BaseGenericCustomDueDatesProps<OccurrenceType> {
    type: "READ_ONLY";
}

type GenericCustomDueDatesProps<OccurrenceType extends string> =
    | EditGenericCustomDueDatesProps<OccurrenceType>
    | ReadOnlyGenericCustomDueDatesProps<OccurrenceType>;

class GenericCustomDueDatesTable<
    OccurrenceType extends string,
> extends React.PureComponent<GenericCustomDueDatesProps<OccurrenceType>> {
    render = () => {
        const {
            translateOccurrence,
            currentOccurrences,
            allOccurrences,
            interval,
            modalVisible,
        } = this.props;

        const renderCustomDueDate = (occurrenceType: OccurrenceType) => {
            let typedProps;
            switch (this.props.type) {
                case "EDIT": {
                    typedProps = {
                        type: "EDIT" as const,
                        onChange: (
                            change: SchedulingOccurrenceChange | undefined
                        ) => {
                            if (this.props.type !== "EDIT") {
                                throw new Error("not editable");
                            }
                            this.props.onChange(occurrenceType, change);
                        },
                    };
                    break;
                }
                case "READ_ONLY": {
                    typedProps = {
                        type: "READ_ONLY" as const,
                    };
                    break;
                }
                default: {
                    return assertNever(this.props);
                }
            }
            return (
                <CustomDueDatesRow
                    occurrenceType={occurrenceType}
                    {...typedProps}
                    value={currentOccurrences[occurrenceType]}
                    interval={interval}
                    modalVisible={modalVisible}
                />
            );
        };

        const renderIcon = (occurrence: OccurrenceType) => {
            const type = currentOccurrences[occurrence].type;
            switch (type) {
                case "AllCustomAndDifferent":
                case "AllActiveAndSomeCustom":
                    return (
                        <Tooltip
                            title={t(
                                "Control_Recurrence_OneOrMoreCustomized_Tooltip"
                            )}
                        >
                            <WarningOutlined
                                className={theme.customDueDateWarningIcon}
                            />
                        </Tooltip>
                    );
                case "AllActiveAndDefault":
                case "AllCustomAndSame":
                case "Mixed":
                case "AllInactive":
                    return <span />;
                default:
                    assertNever(type);
            }
        };

        const data: Array<{ occurrence: OccurrenceType }> = allOccurrences.map(
            (occurrence) => ({ occurrence })
        );

        const columns = [
            {
                title: "occurrence",
                dataIndex: "occurrence",
                key: "occurrence",
                width: "100px",
                render: (occurrence: OccurrenceType) => (
                    <span>{translateOccurrence(occurrence)}</span>
                ),
            },
            {
                title: "occurrence",
                dataIndex: "occurrence",
                key: "customDueDate",
                render: renderCustomDueDate,
            },
            {
                title: "Icon",
                dataIndex: "occurrence",
                key: "icon",
                width: "100px",
                render: renderIcon,
            },
        ];

        return (
            <Table
                dataSource={data}
                columns={columns}
                showHeader={false}
                pagination={false}
                bordered={false}
                size={"middle"}
                rowKey="occurrence"
            />
        );
    };
}

interface ToggleableTooltipProps {
    children: React.ReactNode;
    showTooltip: boolean;
    title: string;
}

const ToggleableTooltip = (props: ToggleableTooltipProps) => {
    if (props.showTooltip) {
        return <Tooltip title={props.title}>{props.children}</Tooltip>;
    } else {
        return <>{props.children}</>;
    }
};

function getInactiveCount(value: RowState<MonthDueDateOffset>): number {
    switch (value.type) {
        case "AllInactive":
            return value.totalCount;
        case "Mixed":
            return value.inactiveCount;
        default:
            return 0;
    }
}

interface BaseCustomDueDatesRowProps<OccurrenceType> {
    occurrenceType: OccurrenceType;
    value: RowState<MonthDueDateOffset>;
    interval: Interval;
    modalVisible: boolean;
}

interface EditCustomDueDatesRowProps<
    OccurrenceType,
> extends BaseCustomDueDatesRowProps<OccurrenceType> {
    type: "EDIT";
    onChange: (change: SchedulingOccurrenceChange | undefined) => void;
}

interface ReadOnlyCustomDueDatesRowProps<
    OccurrenceType,
> extends BaseCustomDueDatesRowProps<OccurrenceType> {
    type: "READ_ONLY";
}

type CustomDueDatesRowProps<OccurrenceType> =
    | EditCustomDueDatesRowProps<OccurrenceType>
    | ReadOnlyCustomDueDatesRowProps<OccurrenceType>;

interface CustomDueDatesRowState {
    popconfirmVisible: boolean;
}

class CustomDueDatesRow<OccurrenceType> extends React.PureComponent<
    CustomDueDatesRowProps<OccurrenceType>,
    CustomDueDatesRowState
> {
    constructor(props: CustomDueDatesRowProps<OccurrenceType>) {
        super(props);

        this.state = {
            popconfirmVisible: false,
        };
    }

    static getDerivedStateFromProps<T>(
        props: CustomDueDatesRowProps<T>,
        state: CustomDueDatesRowState
    ) {
        if (!props.modalVisible && state.popconfirmVisible) {
            return {
                popconfirmVisible: false,
            };
        }
        return null;
    }

    render = () => {
        const { type, value } = this.props;

        if (value.type === "AllCustomAndSame") {
            return this.renderAllCustomAndSame(value.offset);
        } else {
            let activate;
            if (value.type === "AllInactive" || value.type === "Mixed") {
                switch (type) {
                    case "EDIT": {
                        activate = (
                            <Button
                                variant="link"
                                onClick={() => {
                                    if (this.props.type !== "EDIT") {
                                        throw new Error("not editable");
                                    }
                                    this.props.onChange(
                                        "ActivateOnlyDeactivated"
                                    );
                                }}
                            >
                                {t(
                                    "CreateControl_Recurrence_IntervalDetails_Activate"
                                ) + ` (${getInactiveCount(value)})`}
                            </Button>
                        );
                        break;
                    }
                    case "READ_ONLY": {
                        activate = getInactiveCount(value);
                        break;
                    }
                    default: {
                        return assertNever(this.props);
                    }
                }
            } else {
                activate = null;
            }
            return (
                <>
                    {this.renderCustomizeButton()}
                    {activate}
                </>
            );
        }
    };

    renderCustomizeButton = () => {
        const { type, value, modalVisible } = this.props;
        const { popconfirmVisible } = this.state;
        const disabled = value.type === "Mixed" || value.type === "AllInactive";

        switch (value.type) {
            case "AllActiveAndSomeCustom":
            case "AllCustomAndDifferent":
                switch (type) {
                    case "EDIT": {
                        return (
                            <Popconfirm
                                title={
                                    <span>
                                        {renderMarkdown(
                                            t(
                                                "Control_Recurrence_OneOrMoreCustomized_Question"
                                            )
                                        )}
                                    </span>
                                }
                                onConfirm={() => {
                                    if (type !== "EDIT") {
                                        throw new Error("not editable");
                                    }
                                    this.props.onChange({
                                        Custom: DEFAULT_DUE_DATE_OFFSET,
                                    });
                                }}
                                onCancel={() => {}}
                                okText={t("Classification_Mapping_InUse_Yes")}
                                cancelText={t(
                                    "Classification_Mapping_InUse_No"
                                )}
                                open={modalVisible && popconfirmVisible}
                                onOpenChange={(visible) =>
                                    this.setState({
                                        popconfirmVisible: visible,
                                    })
                                }
                            >
                                <Button variant="link" disabled={disabled}>
                                    {t(
                                        "CreateControl_Recurrence_IntervalDetails_Customize"
                                    ) + ` (${value.totalCount})`}
                                </Button>
                            </Popconfirm>
                        );
                    }
                    case "READ_ONLY": {
                        return undefined;
                    }
                    default: {
                        return assertNever(type);
                    }
                }
            case "AllActiveAndDefault":
            case "AllInactive":
            case "Mixed":
            case "AllCustomAndSame":
                switch (type) {
                    case "EDIT": {
                        return (
                            <ToggleableTooltip
                                showTooltip={disabled}
                                title={t(
                                    "Control_Recurrence_NeedActivationFirst_Tooltip"
                                )}
                            >
                                <Button
                                    variant="link"
                                    disabled={disabled}
                                    onClick={() => {
                                        if (type !== "EDIT") {
                                            throw new Error("not editable");
                                        }
                                        this.props.onChange({
                                            Custom: DEFAULT_DUE_DATE_OFFSET,
                                        });
                                    }}
                                >
                                    {t(
                                        "CreateControl_Recurrence_IntervalDetails_Customize"
                                    ) + ` (${value.totalCount})`}
                                </Button>
                            </ToggleableTooltip>
                        );
                    }
                    case "READ_ONLY": {
                        return undefined;
                    }
                    default: {
                        return assertNever(type);
                    }
                }
            default:
                assertNever(value);
        }
    };

    onChangeMonth = (months: number) => {
        const { type, value } = this.props;
        if (type !== "EDIT") {
            throw new Error("not editable");
        }
        const offset = value.type === "AllCustomAndSame" && value.offset;
        if (offset) {
            this.props.onChange({
                Custom: {
                    ...offset,
                    offsetPeriod: { ...offset.offsetPeriod, months },
                },
            });
        }
    };

    onChangeDays = (days: number) => {
        const { type, value } = this.props;
        if (type !== "EDIT") {
            throw new Error("not editable");
        }
        const offset = value.type === "AllCustomAndSame" && value.offset;
        if (offset) {
            this.props.onChange({
                Custom: {
                    ...offset,
                    offsetPeriod: { ...offset.offsetPeriod, days },
                },
            });
        }
    };

    onChangeOffsetType = (offsetType: OffsetType) => {
        const { type, value } = this.props;
        if (type !== "EDIT") {
            throw new Error("not editable");
        }
        const offset = value.type === "AllCustomAndSame" && value.offset;
        if (offset) {
            this.props.onChange({ Custom: { ...offset, offsetType } });
        }
    };

    onChangeAnchor = (anchor: OffsetAnchor) => {
        const { type, value } = this.props;
        if (type !== "EDIT") {
            throw new Error("not editable");
        }
        const offset = value.type === "AllCustomAndSame" && value.offset;
        if (offset) {
            this.props.onChange({ Custom: { ...offset, anchor } });
        }
    };

    renderAllCustomAndSame = (value: MonthDueDateOffset) => {
        const { type, interval } = this.props;
        return (
            <div className={theme.inlineMultiDueDateOffsetContainer}>
                <MultiDefaultDueDateOffsetInput
                    useDefaultCalendar={true}
                    inline={true}
                    calendars={[]}
                    period={interval}
                    type={type}
                    values={{
                        calendarIds: [],
                        months: [value.offsetPeriod.months],
                        days: [value.offsetPeriod.days],
                        offsetTypes: [value.offsetType],
                        anchors: [value.anchor],
                    }}
                    onChangeMonths={this.onChangeMonth}
                    onChangeDays={this.onChangeDays}
                    onChangeCalendarId={() => {}}
                    onChangeOffsetType={this.onChangeOffsetType}
                    onChangeAnchor={this.onChangeAnchor}
                />
                {type === "EDIT" && (
                    <Button
                        size="icon"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            if (type !== "EDIT") {
                                throw new Error("not editable");
                            }
                            this.props.onChange("Default");
                        }}
                    />
                )}
            </div>
        );
    };
}

export { GenericCustomDueDatesModal };
